import { fork, put, takeEvery } from "redux-saga/effects";
import { startLoading, setSuccess, setError } from "../store/appSlice";
import {
  customemersPaymentsProvidersOperationPaths,
  fetchCustomerPaymentsProviders,
  SetCustomerPaymentsProviderActiveFlagProps,
  setCustomerPaymentsProvider,
  setCustomerPaymentsProviders,
  updateCustomerPaymentsProvider,
  setCustomerPaymentsProviderActiveFlag,
} from "../store/customersPaymentsProvidersSlice";
import { UUID } from "../utils";
import { callApi } from "./helpers/api";
import * as CustomersPaymentsProvidersApi from "../api/CustomersPaymentsProviders";
// import * as CustomersPaymentsProvidersApi from "../fakeApi/CustomersPaymentsProviders";
import { CustomerPaymentProvider } from "../models/CustomerPaymentProvider";
import { PayloadAction } from "@reduxjs/toolkit";
import { List } from "../api/helpers/pagination";

function* fetchCustomerPaymentsProvidersSaga({
  payload,
}: {
  payload: {
    customerId: UUID;
  };
  type: string;
}) {
  const { customerId } = payload;
  const { listing: operationPath } = customemersPaymentsProvidersOperationPaths(
    {
      customerId,
    }
  );

  yield put(startLoading(operationPath));

  try {
    const data: List<CustomerPaymentProvider> = yield callApi(
      CustomersPaymentsProvidersApi.getCustomerPaymentProviders,
      customerId
    );
    yield put(
      setCustomerPaymentsProviders({
        list: data.list.map((provider) => {
          return {
            ...provider,
            customerId,
          };
        }),
      })
    );
    yield put(setSuccess(operationPath));
  } catch (error) {
    console.error(error);
    yield put(
      setError({
        path: operationPath,
        error,
      })
    );
  }
}

function* updateCustomerPaymentsProviderSaga({
  payload,
}: {
  payload: {
    customerId: UUID;
    providerId: UUID;
    data: CustomerPaymentProvider;
  };
  type: string;
}) {
  const { customerId, providerId, data } = payload;
  const { update: operationPath } = customemersPaymentsProvidersOperationPaths({
    customerId,
    providerId,
  });

  yield put(startLoading(operationPath));

  try {
    const {
      list: paymentProviders,
    }: List<CustomerPaymentProvider> = yield callApi(
      CustomersPaymentsProvidersApi.putCustomerPaymentProviders,
      customerId,
      providerId,
      data
    );

    const paymentProvider = paymentProviders.find(
      (config) => providerId === config.provider.id
    );

    if (paymentProvider) {
      yield put(
        setCustomerPaymentsProvider({
          customerId,
          providerId,
          item: {
            ...paymentProvider,
            customerId,
          },
        })
      );
    }
    yield put(setSuccess(operationPath));
  } catch (error) {
    console.error(error);
    yield put(
      setError({
        path: operationPath,
        error,
      })
    );
  }
}

function* setCustomerPaymentsProviderActiveFlagSaga(
  action: PayloadAction<SetCustomerPaymentsProviderActiveFlagProps>
) {
  const { customerId, providerId, active } = action.payload;
  const {
    setActiveFlag: operationPath,
  } = customemersPaymentsProvidersOperationPaths({ customerId, providerId });

  yield put(startLoading(operationPath));
  try {
    const paymentProviders: List<CustomerPaymentProvider> = yield callApi(
      CustomersPaymentsProvidersApi.putCustomerPaymentProviders,
      customerId,
      providerId,
      {
        active,
      }
    );

    const updatedPaymentProvider = paymentProviders.list.find(
      (config) =>
        config.customerId === customerId && config.provider.id === providerId
    );

    if (updatedPaymentProvider) {
      yield put(
        setCustomerPaymentsProvider({
          customerId,
          providerId,
          item: updatedPaymentProvider,
        })
      );
    }
    yield put(setSuccess(operationPath));
  } catch (error) {
    console.error(error);
    yield put(
      setError({
        path: operationPath,
        error,
      })
    );
  }
}

function* watchFetchPaymentsProviders() {
  yield takeEvery(
    fetchCustomerPaymentsProviders.type,
    fetchCustomerPaymentsProvidersSaga
  );
}

function* watchUpdatePaymentsProviders() {
  yield takeEvery(
    updateCustomerPaymentsProvider.type,
    updateCustomerPaymentsProviderSaga
  );
}

function* watchSetCustomerPaymentsProviderActiveFlag() {
  yield takeEvery(
    setCustomerPaymentsProviderActiveFlag.type,
    setCustomerPaymentsProviderActiveFlagSaga
  );
}

export function* CustomersPaymentsProvidersSaga() {
  yield fork(watchFetchPaymentsProviders);
  yield fork(watchUpdatePaymentsProviders);
  yield fork(watchSetCustomerPaymentsProviderActiveFlag);
}
